/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ContactDetailsDialogConfig, contactDetailsDialogRoute } from '@gh/builder-shared';
import {
  BuilderPermission,
  ContactType,
  JobOrderDelivery,
  JobOrderMethod,
  jobOrderMethodFromDeliveryMethod,
} from '@gh/builder-shared-data';
import {
  createDialogRoute,
  DateTimeTypes,
  EnumDictionary,
  FormState,
  FormStateBuilder,
  getDialogInitData,
  MaskedTypes,
  toAppFormGroup,
  updateChildrenSchema,
  UserContext,
  whenAllOf,
  whenCreateOrEditMode,
  whenEditMode,
  whenOneOf,
} from '@gh/common-shared';
import { getAutocompleteItemId, ID } from '@gh/common-shared-data';
import { mergeListWithFilteredData } from '@gh/core-data';
import { LocalizationService } from '@gh/core-mls';
import {
  AutocompleteLoadMoreEvent,
  collectValues,
  DialogInput,
  DialogService,
  FormMode,
  getControlValue,
  markControlAsDirty,
  NotificationService,
  setControlValue,
  WaitingService,
} from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';
import { assign, isEmpty, isNil } from 'lodash';
import {
  JobOrderDetailsBusinessService,
  JobOrderDetailsDialogConfig,
} from '../../resources/job-order-details-business.service';
import {
  isJobOrderProduced,
  JobOrderClonedOrder,
  JobOrderDetails,
  JobOrderSupplier,
} from '../../resources/job-order-details.model';

const getOrderChildrenFormSchema = (order: JobOrderDetails) => {
  const editableOrUnlocked = whenOneOf(whenCreateOrEditMode(), order.unlocked || false);
  return {
    orderId: void 0,
    costCenter: { enabled: false },
    orderNumber: { enabled: false },
    allowReprint: { enabled: whenAllOf(whenEditMode(), isJobOrderProduced(order)) },
    supplier: { required: true, enabled: editableOrUnlocked },
    delivery: { enabled: editableOrUnlocked },
    deliveryDate: {
      type: DateTimeTypes.ShortDate,
      enabled: whenAllOf(editableOrUnlocked, order.delivery === JobOrderDelivery.Entered),
    },
    deliveryEnteredDate: void 0,
    deliveryScheduleDate: void 0,
    producedDate: { enabled: editableOrUnlocked },
    method: { enabled: editableOrUnlocked },
    attachments: { enabled: whenCreateOrEditMode() },
    unlocked: void 0,
    version: void 0,
    amount: void 0,
  };
};

@Component({
  standalone: false,
  selector: 'gh-job-order-details-dialog',
  templateUrl: 'job-order-details-dialog.component.html',
  styleUrls: ['job-order-details-dialog.component.scss'],
})
export class JobOrderDetailsDialogComponent implements OnInit, DialogInput<JobOrderDetailsDialogConfig> {
  input: JobOrderDetailsDialogConfig;

  form: FormState<JobOrderDetails>;
  clonedForm: FormState<JobOrderClonedOrder>;

  deliveryDict: EnumDictionary<JobOrderDelivery>;
  methodDict: EnumDictionary<JobOrderMethod>;

  private preferredSuppliers: JobOrderSupplier[];

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              private localizationService: LocalizationService,
              private userContext: UserContext,
              private waitings: WaitingService,
              private notifications: NotificationService,
              private dialogRef: MatDialogRef<any>,
              private business: JobOrderDetailsBusinessService,
              private dialogs: DialogService,
              private formStateBuilder: FormStateBuilder) {

  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_create_order',
      [FormMode.EditMode]: 'lbl_edit_order',
      [FormMode.ViewMode]: 'lbl_view_order',
    }[this.form.mode];
  }

  get orderStatus(): string {
    return isJobOrderProduced(this.form.group.getRawValue()) ? 'lbl_produced' : 'lbl_not_produced';
  }

  get folderId(): ID {
    return this.input.masterInfo.jobDocsFolderId;
  }

  get isUnlocked(): boolean {
    return getControlValue(this.form.group, 'unlocked');
  }

  get canCloneFiles(): boolean {
    return this.clonedForm.editable
      && !isEmpty(getControlValue(this.clonedForm.group, 'costCenterCode'))
      && !isEmpty(getControlValue(this.clonedForm.group, 'orderNumber'));
  }

  get isUnlockable(): boolean {
    const { masterInfo: { isAuthorized, isFinalized, isNotProceeding } } = this.input;

    return this.form.viewMode
      && this.userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.EDIT)
      && isAuthorized && !isFinalized && !isNotProceeding;
  }

  get canUnlock(): boolean {
    return this.isUnlockable && !this.isUnlocked;
  }

  get hasEmailMethod(): boolean {
    const supplier = getControlValue(this.form.group, 'supplier');
    return supplier && supplier.orderBy === JobOrderMethod.Email || false;
  }

  get isJobProduced(): boolean {
    return isJobOrderProduced(this.form.submitValue);
  }

  ngOnInit(): void {
    const { order, ...init } = getDialogInitData(this.dialogData);

    const hasEditPermission = this.userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.EDIT);
    const { isAuthorized, isFinalized, isNotProceeding } = this.input.masterInfo;
    const mode = !hasEditPermission || (isAuthorized || isFinalized || isNotProceeding)
      ? FormMode.ViewMode
      : (isNil(this.input.order.orderId) ? FormMode.CreateMode : FormMode.EditMode);

    assign(this, init);

    this.form = this.formStateBuilder.buildPlain({
      mode,
      value: order,
      children: getOrderChildrenFormSchema(order),
    });

    this.clonedForm = this.formStateBuilder.buildPlain({
      mode,
      enabled: whenCreateOrEditMode(),
      children: {
        costCenterCode: { type: MaskedTypes.CostCenterCode },
        orderNumber: { type: MaskedTypes.OrderNumber },
      },
    });

    this.preferredSuppliers = this.input.preferredSuppliers.map((supplier) => ({ ...supplier, isPreferred: true }));
  }

  loadSuppliers(event: AutocompleteLoadMoreEvent): void {
    const stringHelper = this.localizationService.getStringL10nHelper();
    // filter preferred suppliers and merge with suppliers loaded from the server
    event.load(mergeListWithFilteredData(
      this.preferredSuppliers.filter((supplier) => stringHelper.includesIgnoreCase(supplier.name, event.query.filter)),
      event.query,
      (query) => this.business.findAllSuppliersByName(query, this.preferredSuppliers.map(getAutocompleteItemId))));
  }

  onSupplierChanged(): void {
    this.fixOrderMethod();
  }

  onSupplierCreate(): void {
    this.openSupplier();
  }

  onSupplierOpen(): void {
    this.openSupplier(getControlValue(this.form.group, 'supplier'));
  }

  onUnlock(): void {
    setControlValue(this.form.group, 'unlocked', true);
    this.updateForm();
  }

  onDeliveryChange(delivery: JobOrderDelivery): void {
    setControlValue(
      this.form.group,
      'deliveryDate',
      delivery === JobOrderDelivery.Entered
        ? getControlValue(this.form.group, 'deliveryEnteredDate')
        : getControlValue(this.form.group, 'deliveryScheduleDate'));
    this.updateForm();
  }

  onCloneFiles(): void {
    const ccCode = Number(getControlValue(this.clonedForm.group, 'costCenterCode'));
    const orderNumber = Number(getControlValue(this.clonedForm.group, 'orderNumber'));

    this.input
      .findAllOrderAttachments(ccCode, orderNumber)
      .subscribe(
        (files) => setControlValue(this.form.group, 'attachments', files),
        this.notifications.processAll);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isUnlockable) {
      this.business.save(this.input.masterInfo.jobId, this.form.submitValue).pipe(
        this.waitings.operator())
        .subscribe(
          (order) => this.dialogRef.close(order),
          this.notifications.processAll);
    } else {
      this.dialogRef.close(this.form.submitValue);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private openSupplier(openedSupplier?: JobOrderSupplier): void {
    this.dialogs.openWithInput(contactDetailsDialogRoute, <ContactDetailsDialogConfig>{
      entryPoint: 'job-order',
      contactType: ContactType.CreditorSupplier,
      contactId: openedSupplier ? openedSupplier.id : void 0,
      parentTitle: this.title,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((contact: any) => {
        setControlValue(this.form.group, 'supplier', {
          id: contact.contactId,
          name: contact.name,
          orderBy: contact.orderBy,
        });
        markControlAsDirty(this.form.group, 'supplier');
        this.fixOrderMethod();
      });
  }

  private updateForm(): void {
    updateChildrenSchema(toAppFormGroup(this.form.group), getOrderChildrenFormSchema(collectValues(this.form.group)));
  }

  /**
   * Guarantees that Email method is selected only if it is allowed for current supplier
   */
  private fixOrderMethod(): void {
    const supplier = getControlValue(this.form.group, 'supplier');
    // reset method to 'Printer' if 'Email' is not allowed for selected supplier
    setControlValue(this.form.group, 'method', isNil(supplier) ?
      JobOrderMethod.Printer :
      supplier.orderBy === JobOrderMethod.Printer ?
        supplier.orderBy :
        jobOrderMethodFromDeliveryMethod(supplier.orderBy));
  }
}

export const jobOrderDetailsDialogRoute = createDialogRoute({
  component: JobOrderDetailsDialogComponent,
  init: JobOrderDetailsBusinessService,
  config: { width: '400px', minHeight: '500px', maxHeight: '650px' },
});
